import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { Loader2, RefreshCw, PenLine, CheckCircle2, XCircle, AlertTriangle, Minus, Shield, FileText, TrendingUp, ListChecks, AlertCircle, UserCheck, Clock, Wrench, ChevronDown, ChevronRight } from "lucide-react";

const VERDICT_COLORS = {
  PASS: "bg-emerald-500/10 text-emerald-600 border-emerald-200",
  PARTIAL: "bg-amber-500/10 text-amber-600 border-amber-200",
  FAIL: "bg-red-500/10 text-red-600 border-red-200",
  INCONCLUSIVE: "bg-slate-100 text-slate-500 border-slate-200",
};

const VERDICT_ICONS = {
  PASS: CheckCircle2,
  PARTIAL: AlertTriangle,
  FAIL: XCircle,
  INCONCLUSIVE: Minus,
};

function InfoRow({ label, value, color }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between text-sm py-0.5">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-medium ${color || ""}`}>{value}</span>
    </div>
  );
}

function ReasoningList({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="space-y-1 mt-2">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Gaps Identified</p>
      <ul className="space-y-1">
        {items.map((r, i) => (
          <li key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <AlertCircle className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />
            <span>{r}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PillarLabel({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="w-4 h-4 text-muted-foreground" />
      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
    </div>
  );
}

function DesignCard({ data }) {
  const [showScoring, setShowScoring] = useState(false);
  const VerdictIcon = VERDICT_ICONS[data.verdict] || Minus;
  const vc = VERDICT_COLORS[data.verdict] || VERDICT_COLORS.INCONCLUSIVE;

  // Prefer design_findings (new field) over legacy reasoning
  const findings = (data.design_findings?.length > 0 ? data.design_findings : data.reasoning) || [];

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-2">
        <PillarLabel icon={Shield} label="Design Effectiveness" />
      </CardHeader>
      <CardContent className="flex-1 space-y-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={vc}><VerdictIcon className="w-3 h-3 mr-1" />{data.verdict}</Badge>
          <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">Score: {data.score}/100</Badge>
          {data.residual_risk && (
            <Badge variant="outline" className={`ml-auto text-[10px] ${data.residual_risk === "High" ? "bg-red-50 text-red-600 border-red-200" : data.residual_risk === "Medium" ? "bg-amber-50 text-amber-600 border-amber-200" : "bg-emerald-50 text-emerald-600 border-emerald-200"}`}>
              Residual Risk: {data.residual_risk}
            </Badge>
          )}
        </div>

        {findings.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Audit Findings</p>
            <ul className="space-y-2">
              {findings.map((finding, i) => (
                <li key={i} className="flex items-start gap-1.5 text-xs">
                  <AlertCircle className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />
                  <span className="text-slate-700 leading-relaxed">{finding}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <Collapsible open={showScoring} onOpenChange={setShowScoring}>
          <CollapsibleTrigger className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors">
            {showScoring ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Internal Scoring Detail
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs border rounded-md p-2 bg-muted/30">
              <span className="text-muted-foreground">Risk Coverage</span>
              <span className="font-medium text-right">{data.assessment?.risk_coverage || "N/A"}</span>
              <span className="text-muted-foreground">Control Type</span>
              <span className="font-medium text-right">{data.assessment?.control_type_appropriateness || "N/A"}</span>
              <span className="text-muted-foreground">Frequency</span>
              <span className="font-medium text-right">{data.assessment?.frequency_adequacy || "N/A"}</span>
              <span className="text-muted-foreground">Ownership Defined</span>
              <span className="font-medium text-right">{data.assessment?.ownership_defined ? "Yes" : "No"}</span>
              <span className="text-muted-foreground">Evidence Quality</span>
              <span className="font-medium text-right">{data.assessment?.evidence_generation || "N/A"}</span>
            </div>
          </CollapsibleContent>
        </Collapsible>

      </CardContent>
    </Card>
  );
}

function EvidenceSection({ sampledRecords = [], dataSourcesUsed = [], queryDescription = "" }) {
  const [open, setOpen] = useState(false);
  if (!sampledRecords.length && !dataSourcesUsed.length && !queryDescription) return null;

  const passCount = sampledRecords.filter(r => r.status === "pass").length;
  const failCount = sampledRecords.filter(r => r.status === "fail").length;
  const skipCount = sampledRecords.filter(r => r.status === "skip").length;

  return (
    <div className="mt-2 border rounded-md">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex items-center justify-between w-full p-2 hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-2">
            <FileText className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Evidence Chain of Custody</span>
          </div>
          {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
        </CollapsibleTrigger>
        <CollapsibleContent className="px-2 pb-2 space-y-2">
          {queryDescription && (
            <p className="text-xs text-muted-foreground">{queryDescription}</p>
          )}
          {dataSourcesUsed.length > 0 && (
            <div>
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Data Sources</p>
              <div className="flex flex-wrap gap-1">
                {dataSourcesUsed.map((src, i) => (
                  <Badge key={i} variant="secondary" className="text-[10px]">{src}</Badge>
                ))}
              </div>
            </div>
          )}
          {sampledRecords.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Sampled Records</p>
                <span className="text-[10px] text-muted-foreground">({passCount} pass, {failCount} fail{skipCount > 0 ? `, ${skipCount} skip` : ""})</span>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {sampledRecords.map((rec, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs py-1 px-2 rounded bg-muted/30">
                    <span className={`shrink-0 mt-0.5 ${rec.status === "pass" ? "text-emerald-500" : rec.status === "fail" ? "text-red-500" : "text-slate-400"}`}>
                      {rec.status === "pass" ? <CheckCircle2 className="w-3 h-3" /> : rec.status === "fail" ? <XCircle className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{rec.record_id || `Record ${i + 1}`}</span>
                        <Badge variant={rec.status === "pass" ? "default" : rec.status === "fail" ? "destructive" : "secondary"} className="text-[9px] px-1 py-0 h-4">
                          {rec.status.toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground text-[11px] truncate">{rec.reason}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function OperatingCard({ data }) {
  const VerdictIcon = VERDICT_ICONS[data.verdict] || Minus;
  const vc = VERDICT_COLORS[data.verdict] || VERDICT_COLORS.INCONCLUSIVE;
  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-2">
        <PillarLabel icon={ListChecks} label="Operating Effectiveness" />
      </CardHeader>
      <CardContent className="flex-1 space-y-3">
        <Badge variant="outline" className={vc}><VerdictIcon className="w-3 h-3 mr-1" />{data.verdict}</Badge>

        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <span className="text-muted-foreground">Population</span>
          <span className="font-medium text-right">{data.population}</span>
          <span className="text-muted-foreground">Sample Size</span>
          <span className="font-medium text-right">{data.sample_size}</span>
          <span className="text-muted-foreground">Exceptions</span>
          <span className="font-medium text-right text-red-600">{data.exceptions}</span>
          <span className="text-muted-foreground">Exception Rate</span>
          <span className="font-medium text-right">{data.exception_rate}</span>
        </div>

        {data.testing_procedure?.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Testing Procedure</p>
            <ul className="space-y-1">
              {data.testing_procedure.map((s, i) => (
                <li key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                  <span className="text-emerald-500 mt-0.5 shrink-0">{i + 1}.</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {data.evidence_reviewed?.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Evidence Reviewed</p>
            <div className="flex flex-wrap gap-1.5">
              {data.evidence_reviewed.map((e, i) => (
                <Badge key={i} variant="secondary" className="text-[10px]">{e}</Badge>
              ))}
            </div>
          </div>
        )}

        {data.conclusion && (
          <div className="bg-muted/50 rounded-md p-2.5 mt-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Conclusion</p>
            <p className="text-sm font-medium">{data.conclusion}</p>
          </div>
        )}

        <EvidenceSection
          sampledRecords={data.sampled_records || []}
          dataSourcesUsed={data.data_sources_used || []}
          queryDescription={data.query_description || ""}
        />
      </CardContent>
    </Card>
  );
}

// function OutcomeCard({ data }) {
//   const VerdictIcon = VERDICT_ICONS[data.verdict] || Minus;
//   const vc = VERDICT_COLORS[data.verdict] || VERDICT_COLORS.INCONCLUSIVE;
//   return (
//     <Card className="flex flex-col">
//       <CardHeader className="pb-2">
//         <PillarLabel icon={TrendingUp} label="Outcome Effectiveness" />
//       </CardHeader>
//       <CardContent className="flex-1 space-y-3">
//         <Badge variant="outline" className={vc}><VerdictIcon className="w-3 h-3 mr-1" />{data.verdict}</Badge>

//         {data.kpi && (
//           <div>
//             <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">KPI</p>
//             <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
//               <span className="text-muted-foreground">Metric</span>
//               <span className="font-medium text-right">{data.kpi.metric || "N/A"}</span>
//               <span className="text-muted-foreground">Value</span>
//               <span className="font-medium text-right">{data.kpi.value !== null && data.kpi.value !== undefined ? `${data.kpi.value}%` : "N/A"}</span>
//               <span className="text-muted-foreground">Trend</span>
//               <span className="font-medium text-right">{data.kpi.trend || "Unknown"}</span>
//             </div>
//           </div>
//         )}

//         {data.assessment && (
//           <div className="bg-muted/50 rounded-md p-2.5 mt-1">
//             <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Assessment</p>
//             <p className="text-sm">{data.assessment}</p>
//           </div>
//         )}
//       </CardContent>
//     </Card>
//   );
// }

export default function ControlTestingBoard({
  report,
  loading = false,
  onRecompute,
  onOverride,
  disableRecompute = false,
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Computing control testing report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-sm text-muted-foreground">
          No testing report available. Click <span className="font-medium">Compute</span> to generate one.
        </p>
      </div>
    );
  }

  const ov = report.overall_assessment || {};
  const overallColor = VERDICT_COLORS[ov.verdict] || VERDICT_COLORS.INCONCLUSIVE;
  const OverallIcon = VERDICT_ICONS[ov.verdict] || Minus;

  return (
    <div className="space-y-6">

      {report.control_id && (
        <Card>
          <CardContent className="py-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 items-start">
              <div className="lg:col-span-2 space-y-2">
                <div className="space-y-0.5">
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider bg-slate-100 px-2 py-0.5 rounded">
                    {report.control_id}
                  </span>
                  <h2 className="text-lg font-bold text-slate-900 mt-1">{report.control_name || "Unnamed Control"}</h2>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{report.control?.description || "No description"}</p>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs pt-4 lg:pt-0 border-t lg:border-t-0 lg:border-l lg:pl-6 border-slate-100">
                <span className="text-muted-foreground">Regulation</span>
                <span className="font-semibold text-slate-800 text-right">{report.regulation || "N/A"}</span>
                <span className="text-muted-foreground">Domain</span>
                <span className="font-semibold text-slate-800 text-right">{report.domain || "N/A"}</span>
                <span className="text-muted-foreground">Control Type</span>
                <span className="font-semibold text-slate-800 text-right">{report.control?.control_type || "N/A"}</span>
                <span className="text-muted-foreground">Frequency</span>
                <span className="font-semibold text-slate-800 text-right">{report.control?.frequency || "N/A"}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {report.risk?.title && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardContent className="py-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">{report.risk.title}</p>
                {report.risk.impact && <p className="text-xs text-amber-700/80 mt-0.5">{report.risk.impact}</p>}
                <Badge variant="outline" className="mt-1 bg-amber-100/50 text-amber-700 border-amber-300 text-[10px]">
                  Inherent Risk: {report.risk.inherent_risk_rating}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {ov.verdict && (
        <Card className={`border-2 ${overallColor.split(" ")[1]} bg-opacity-5`}>
          <CardContent className="flex items-center gap-4 py-4">
            <div className={`p-2 rounded-full ${overallColor.split(" ")[0]}`}>
              <OverallIcon className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className={`text-sm px-3 py-1 ${overallColor}`}>{ov.verdict}</Badge>
                <span className="text-sm font-semibold">Overall Assessment</span>
                {ov.residual_risk && (
                  <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200 ml-auto">
                    Residual Risk: {ov.residual_risk}
                  </Badge>
                )}
              </div>
              {ov.executive_summary && (
                <p className="text-sm text-muted-foreground">{ov.executive_summary}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {!disableRecompute && onRecompute && (
                <Button variant="outline" size="sm" onClick={onRecompute}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Re-compute
                </Button>
              )}
              {onOverride && (
                <Button variant="outline" size="sm" onClick={onOverride}>
                  <PenLine className="w-3.5 h-3.5 mr-1.5" /> Override
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DesignCard data={report.design_effectiveness || {}} />
        <OperatingCard data={report.operating_effectiveness || {}} />
        {/* <OutcomeCard data={report.outcome_effectiveness || {}} /> */}
      </div>
    </div>
  );
}
